import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'

import {
  PosthogFeatureFlags,
  usePosthogFlag,
  usePosthogFlagsSettled,
} from '@/lib/posthog'
import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/store/auth'

import { McpConnectorsApi } from '../modules/mcpConnectors'

import type {
  IMcpConnectionList,
  IMcpConnectorList,
  IMcpConnectStart,
} from '../types/mcpConnectors'
import type { AxiosError } from 'axios'

export const mcpConnectorQueryKeys = {
  all: ['mcpConnectors'] as const,
  connectors: () => [...mcpConnectorQueryKeys.all, 'catalog'] as const,
  connections: () => [...mcpConnectorQueryKeys.all, 'connections'] as const,
}

export const useMcpConnectorQueries = () => {
  const { isAuthenticated } = storeToRefs(useAuthStore())
  const isMcpConnectorsEnabled = usePosthogFlag(
    PosthogFeatureFlags.McpConnectors,
  )
  const haveFeatureFlagsSettled = usePosthogFlagsSettled()

  // Held until PostHog reports flags, so the flag being off means no request.
  const isQueryEnabled = computed(
    () =>
      isAuthenticated.value &&
      haveFeatureFlagsSettled.value &&
      isMcpConnectorsEnabled.value,
  )

  /** The connector directory with the caller's connection state. */
  const connectors = () =>
    useQuery<IMcpConnectorList, AxiosError>({
      queryKey: mcpConnectorQueryKeys.connectors(),
      queryFn: ({ signal }) => McpConnectorsApi.listConnectors({ signal }),
      enabled: isQueryEnabled,
    })

  /**
   * The caller's active connections. A failure stays silent: the chat input
   * reads this on every page and simply offers no connectors without it.
   */
  const connections = (isEnabled: MaybeRefOrGetter<boolean> = true) =>
    useQuery<IMcpConnectionList, AxiosError>({
      queryKey: mcpConnectorQueryKeys.connections(),
      queryFn: ({ signal }) => McpConnectorsApi.listConnections({ signal }),
      enabled: computed(() => isQueryEnabled.value && toValue(isEnabled)),
    })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: mcpConnectorQueryKeys.all })

  /** Errors are the caller's to show: the connect flow reports them in place. */
  const connect = useMutation<IMcpConnectStart, AxiosError, string>({
    mutationFn: (slug) => McpConnectorsApi.connect(slug),
  })

  const disconnect = useMutation<void, AxiosError, string>({
    mutationFn: (connectionId) => McpConnectorsApi.disconnect(connectionId),
    onSuccess: invalidate,
  })

  return { connectors, connections, connect, disconnect, invalidate }
}
